import { useCallback, useEffect, useMemo, useState } from 'react';

import { useWorkflowStep } from '@/context';
import type { LayoutPreset } from '@/visualization/services/viewport-layout-service';
import type { WorkflowConfig, IWorkflowStep } from '@/workflows/types';

export interface UseViewerLayoutResult {
    /** Currently selected layout preset (null when active step uses a component mainView) */
    currentLayout: LayoutPreset | null;
    /** Available layouts for the workflow */
    availableLayouts: LayoutPreset[];
    /** Change to a different layout by name */
    setLayout: (layoutName: string) => void;
    /** The currently active workflow step */
    activeStep: IWorkflowStep;
    /** Whether the active step replaces the viewport with a component */
    isComponentStep: boolean;
}

/**
 * Hook for managing viewer layout state, driven by the active workflow step.
 *
 * When the active step has a layout mainView, the layout is applied.
 * When the active step has a component mainView, `currentLayout` is null.
 * Switching to a layout step auto-applies its default layout.
 */
export function useViewerLayout(config: WorkflowConfig): UseViewerLayoutResult {
    const { activeStepId } = useWorkflowStep();

    // Build a lookup map from config.layouts
    const layoutMap = useMemo(() => {
        const map = new Map<string, LayoutPreset>();
        for (const preset of config.layouts) {
            map.set(preset.name, preset);
        }
        return map;
    }, [config.layouts]);

    // Build a step lookup
    const stepMap = useMemo(() => {
        const map = new Map<string, IWorkflowStep>();
        for (const step of config.steps) {
            map.set(step.id, step);
        }
        return map;
    }, [config.steps]);

    const activeStep = useMemo(() => {
        const step = stepMap.get(activeStepId);
        if (!step) {
            console.warn('[useViewerLayout] activeStepId not found in config, falling back to first step', {
                activeStepId,
                availableStepIds: [...stepMap.keys()],
            });
            return config.steps[0];
        }
        return step;
    }, [activeStepId, stepMap, config.steps]);

    const isComponentStep = activeStep.mainView.type === 'component';

    // Resolve the default layout name — computed once from the initial step config
    const defaultLayoutName = useMemo(() => {
        const defaultStep = stepMap.get(config.defaultStepId) ?? config.steps[0];
        return defaultStep.mainView.type === 'layout'
            ? defaultStep.mainView.layoutName
            : config.layouts[0].name;
    }, [stepMap, config.defaultStepId, config.layouts, config.steps]);

    const [currentLayoutName, setCurrentLayoutName] = useState<string>(defaultLayoutName);

    // When switching to a layout step, apply its default layout
    useEffect(() => {
        const step = stepMap.get(activeStepId) ?? config.steps[0];
        if (step.mainView.type === 'layout') {
            setCurrentLayoutName(step.mainView.layoutName);
        }
    }, [activeStepId, stepMap, config.steps]);

    const availableLayouts = useMemo(() => [...config.layouts], [config.layouts]);

    const currentLayout = useMemo(() => {
        const resolved = layoutMap.get(currentLayoutName) ?? null;
        if (!resolved) {
            console.warn('[useViewerLayout] currentLayoutName not found in layoutMap', {
                currentLayoutName,
                availableLayouts: [...layoutMap.keys()],
            });
        }
        return resolved;
    }, [isComponentStep, currentLayoutName, layoutMap]);

    const setLayout = useCallback(
        (layoutName: string) => {
            if (layoutMap.has(layoutName)) {
                setCurrentLayoutName(layoutName);
            } else {
                console.warn(`[useViewerLayout] Unknown layout: ${layoutName}`);
            }
        },
        [layoutMap]
    );

    return {
        currentLayout,
        availableLayouts,
        setLayout,
        activeStep,
        isComponentStep,
    };
}
